import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiCheck, FiSearch } from "react-icons/fi";
import "./EditUser.css";

const EditUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL;

  const [user, setUser] = useState(null);
  const [publishers, setPublishers] = useState([]);

  const [selectedPublisherIds, setSelectedPublisherIds] = useState([]);

  const [search, setSearch] = useState("");

  const [loadingUser, setLoadingUser] = useState(true);
  const [loadingPublishers, setLoadingPublishers] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // =========================================================
  // GET ACCOUNT USERS
  // =========================================================

  const getAccountUsers = async () => {
    try {
      setLoadingUser(true);
      setError("");

      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${API_BASE_URL}/api/admin/getAccountUsers`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
          },
        }
      );

    //   console.log("getAccountUsers response:", response.data);

      if (
        response.data &&
        response.data.status === "1" &&
        Array.isArray(response.data.data)
      ) {
        const selectedUser = response.data.data.find(
          (item) => String(item.id) === String(id)
        );

        if (!selectedUser) {
          setError("User not found.");
          setUser(null);
          return;
        }

        setUser(selectedUser);

        // ---------------------------------------------------
        // Convert pub_account_ids string into array
        // ---------------------------------------------------
        //
        // API:
        //
        // "'id1','id2','id3'"
        //
        // becomes:
        //
        // ["id1", "id2", "id3"]
        //
        // ---------------------------------------------------

        const pubAccountIds = parsePublisherIds(
          selectedUser.pub_account_ids
        );

        setSelectedPublisherIds(pubAccountIds);
      } else {
        setError(
          response.data?.message ||
            "Unable to fetch user information."
        );
      }
    } catch (err) {
      console.error("getAccountUsers error:", err);

      setError(
        err.response?.data?.message ||
          "Unable to fetch user information."
      );
    } finally {
      setLoadingUser(false);
    }
  };

  // =========================================================
  // PARSE PUBLISHER IDS
  // =========================================================

  const parsePublisherIds = (value) => {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.map((item) =>
        String(item).trim().replace(/^['"]|['"]$/g, "")
      );
    }

    return String(value)
      .split(",")
      .map((item) =>
        item.trim().replace(/^['"]|['"]$/g, "")
      )
      .filter(Boolean);
  };

  // =========================================================
  // GET ALL PUBLISHERS
  // =========================================================

  const getAllPublishers = async () => {
    try {
      setLoadingPublishers(true);

      const token = localStorage.getItem("token");

      const response = await axios.get(
        `${API_BASE_URL}/api/admin/getAllPublishers`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
          },
        }
      );

    //   console.log(
    //     "getAllPublishers response:",
    //     response.data
    //   );

      if (
        response.data &&
        response.data.status === "1" &&
        Array.isArray(response.data.data)
      ) {
        setPublishers(response.data.data);
      } else {
        setPublishers([]);
      }
    } catch (err) {
      console.error("getAllPublishers error:", err);

      setError(
        err.response?.data?.message ||
          "Unable to fetch publishers."
      );
    } finally {
      setLoadingPublishers(false);
    }
  };

  // =========================================================
  // LOAD DATA
  // =========================================================

  useEffect(() => {
    getAccountUsers();
    getAllPublishers();
  }, [id]);

  // =========================================================
  // GET PUBLISHER ID
  // =========================================================

  const getPublisherId = (publisher) => {
    // Adjust this if your publisher API uses a different field.
    return publisher.account_id;
  };

  // =========================================================
  // GET PUBLISHER NAME
  // =========================================================

  const getPublisherName = (publisher) => {
    return publisher.account_name || "-";
};

  // =========================================================
  // CHECK / UNCHECK PUBLISHER
  // =========================================================

  const handlePublisherChange = (publisherId) => {
    const idString = String(publisherId);

    setSelectedPublisherIds((previous) => {
      if (previous.includes(idString)) {
        return previous.filter(
          (id) => id !== idString
        );
      }

      return [...previous, idString];
    });

    setSuccess("");
  };

  // =========================================================
  // SELECT ALL
  // =========================================================

  const handleSelectAll = () => {
    const filteredIds = filteredPublishers
      .map((publisher) => getPublisherId(publisher))
      .filter(Boolean)
      .map((publisherId) => String(publisherId));

    setSelectedPublisherIds((previous) => {
      const allSelected = filteredIds.every((publisherId) =>
        previous.includes(publisherId)
      );

      if (allSelected) {
        return previous.filter(
          (id) => !filteredIds.includes(id)
        );
      }

      return Array.from(
        new Set([...previous, ...filteredIds])
      );
    });

    setSuccess("");
  };

  // =========================================================
  // FILTER PUBLISHERS
  // =========================================================

  const filteredPublishers = publishers.filter(
    (publisher) => {
      const publisherName =
        getPublisherName(publisher).toLowerCase();

      const publisherId = String(
        getPublisherId(publisher) || ""
      ).toLowerCase();

      const searchText = search.toLowerCase();

      return (
        publisherName.includes(searchText) ||
        publisherId.includes(searchText)
      );
    }
  );

  // =========================================================
  // SAVE
  // =========================================================

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const token = localStorage.getItem("token");

      // -----------------------------------------------------
      // API expects:
      //
      // "'id1', 'id2', 'id3'"
      // -----------------------------------------------------

      const pubAccountIds = selectedPublisherIds
        .map((publisherId) => `'${publisherId}'`)
        .join(", ");

      const payload = {
        user_id: String(id),
        pub_account_ids: pubAccountIds,
      };

      console.log(
        "assignPubToUser payload:",
        payload
      );

      const response = await axios.post(
        `${API_BASE_URL}/api/admin/assignPubToUser`,
        payload,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
          },
        }
      );

      console.log(
        "assignPubToUser response:",
        response.data
      );

      if (
        response.data &&
        (response.data.status === "1" ||
          response.data.status === 1 ||
          response.data.success === true)
      ) {
        setSuccess(
          response.data.message ||
            "Publishers assigned successfully."
        );
        // Refresh user information after save
        await getAccountUsers();
        setTimeout(() => {
            navigate("/user-list");
        }, 1000);
      } else {
        setError(
          response.data?.message ||
            "Unable to update publisher assignment."
        );
      }
    } catch (err) {
      console.error(
        "assignPubToUser error:",
        err
      );

      setError(
        err.response?.data?.message ||
          "Unable to update publisher assignment."
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // CANCEL
  // =========================================================

  const handleCancel = () => {
    navigate("/user-list");
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loadingUser || loadingPublishers) {
    return (
      <div className="edit-user-page">
        <div className="edit-user-container">
          <div className="edit-user-loading">
            Loading user information...
          </div>
        </div>
      </div>
    );
  }

  // =========================================================
  // USER NOT FOUND
  // =========================================================

  if (!user) {
    return (
      <div className="edit-user-page">
        <div className="edit-user-container">

          <div className="edit-user-error">
            {error || "User not found."}
          </div>

          <button
            className="back-user-btn"
            onClick={handleCancel}
          >
            <FiArrowLeft />
            Back to Users
          </button>

        </div>
      </div>
    );
  }

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <div className="edit-user-page">

      <div className="edit-user-container">

        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <div className="edit-user-header">

          <div className="edit-user-title-section">

            <button
              type="button"
              className="back-icon-btn"
              onClick={handleCancel}
              title="Back"
            >
              <FiArrowLeft size={20} />
            </button>

            <div>
              <h2>Edit User</h2>

              <p>
                Manage publishers assigned to this user
              </p>
            </div>

          </div>

        </div>

        {/* ================================================= */}
        {/* USER INFORMATION */}
        {/* ================================================= */}

        <div className="user-information">

          <div className="user-info-item">

            <span className="user-info-label">
              Name
            </span>

            <span className="user-info-value">
              {user.name || "-"}
            </span>

          </div>

          <div className="user-info-item">

            <span className="user-info-label">
              Username
            </span>

            <span className="user-info-value">
              {user.username || "-"}
            </span>

          </div>

        </div>

        {/* ================================================= */}
        {/* MESSAGES */}
        {/* ================================================= */}

        {error && (
          <div className="edit-user-error">
            {error}
          </div>
        )}

        {success && (
          <div className="edit-user-success">
            {success}
          </div>
        )}

        {/* ================================================= */}
        {/* PUBLISHER SECTION */}
        {/* ================================================= */}

        <div className="publisher-section">

          <div className="publisher-section-header">

            <div>
              <h3>
                Assign Publishers
              </h3>

              <p>
                Select publishers that this user can
                access.
              </p>
            </div>

            <div className="selected-count">
              {selectedPublisherIds.length} selected
            </div>

          </div>

          {/* ============================================= */}
          {/* SEARCH + SELECT ALL */}
          {/* ============================================= */}

          <div className="publisher-toolbar">

            <div className="publisher-search">

              <FiSearch size={17} />

              <input
                type="text"
                placeholder="Search publisher..."
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
              />

            </div>

            {filteredPublishers.length > 0 && (
              <button
                type="button"
                className="select-all-btn"
                onClick={handleSelectAll}
              >
                {filteredPublishers.every(
                  (publisher) =>
                    selectedPublisherIds.includes(
                      String(
                        getPublisherId(publisher)
                      )
                    )
                )
                  ? "Unselect All"
                  : "Select All"}
              </button>
            )}

          </div>

          {/* ============================================= */}
          {/* PUBLISHER LIST */}
          {/* ============================================= */}

                  <div className="publisher-list">
                      {filteredPublishers.length > 0 ? (
                          filteredPublishers.map((publisher) => {
                              const publisherId = String(publisher.account_id);
                              const publisherName = publisher.account_name || "-";

                              const isChecked =
                                  selectedPublisherIds.includes(publisherId);

                              return (
                                  <label
                                      key={publisherId}
                                      className={`publisher-checkbox-row ${isChecked ? "publisher-selected" : ""
                                          }`}
                                  >
                                      <input
                                          type="checkbox"
                                          checked={isChecked}
                                          onChange={() =>
                                              handlePublisherChange(publisherId)
                                          }
                                      />

                                      <span className="custom-checkbox">
                                          {isChecked && <FiCheck size={13} />}
                                      </span>

                                      <span className="publisher-name">
                                          {publisherName}
                                      </span>
                                  </label>
                              );
                          })
                      ) : (
                          <div className="no-publishers">
                              {search
                                  ? "No publishers found."
                                  : "No publishers available."}
                          </div>
                      )}
                  </div>

        </div>

        {/* ================================================= */}
        {/* FOOTER */}
        {/* ================================================= */}

        <div className="edit-user-footer">

          <button
            type="button"
            className="cancel-user-btn"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            type="button"
            className="save-user-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? "Saving..."
              : "Save Changes"}
          </button>

        </div>

      </div>

    </div>
  );
};

export default EditUser;